import React, { useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { formatDate as fmtDateUtil } from '../../utils/dateUtils';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { LoadingState, StatCard, ReportTable, COLORS, fmt } from './ReportHelpers';

const fmtDate = (d) => fmtDateUtil(d);

export function LostQuotesTab({ data, loading, onExport }) {
  if (loading) return <LoadingState />;
  if (!data) return null;

  const { total_lost, cancelled_count, declined_count, total_value_lost, reasons_chart, monthly_trend, items } = data;

  const handleExport = () => {
    const headers = ['Quote No.', 'Date', 'Client', 'Job Titles', 'Status', 'Reason', 'Value'];
    const rows = items.map(r => [r.quote_number, r.date, r.client_name, r.job_titles, r.status, r.reason, r.value]);
    const summary = ['', '', '', '', '', 'TOTAL LOST', fmt(total_value_lost)];
    onExport('Lost Quotes Report', headers, rows, summary);
  };

  const topReasons = reasons_chart.filter(r => r.name !== 'No reason provided');
  const noReasonCount = reasons_chart.find(r => r.name === 'No reason provided')?.value || 0;

  return (
    <div className="space-y-6" data-testid="lost-quotes-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Lost Quotes" value={total_lost} sub={`${cancelled_count} cancelled, ${declined_count} declined`} />
        <StatCard label="Total Value Lost" value={fmt(total_value_lost)} color="text-green-600" />
        <StatCard label="Cancelled" value={cancelled_count} sub="Internally cancelled" />
        <StatCard label="Declined" value={declined_count} sub="Customer declined" />
      </div>

      <div className="flex justify-end print:hidden">
        <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700" data-testid="export-lost-quotes-excel">
          <FileSpreadsheet size={14} /> Export Excel
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {reasons_chart.length > 0 && (
          <div className="bg-white rounded-[10px] border p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Loss Reasons Breakdown</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={reasons_chart} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value"
                  label={({ name, value }) => `${name.length > 20 ? name.slice(0, 20) + '...' : name}: ${value}`}>
                  {reasons_chart.map((entry, i) => <Cell key={`cell-${entry.name || i}`} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {monthly_trend.length > 0 && (
          <div className="bg-white rounded-[10px] border p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Monthly Loss Trend</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="cancelled" name="Cancelled" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="declined" name="Declined" fill="#e53e3e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {topReasons.length > 0 && (
        <div className="bg-white rounded-[10px] border p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Loss Reasons</h3>
          <div className="space-y-2">
            {topReasons.map((r, i) => {
              const pct = total_lost > 0 ? Math.round((r.value / total_lost) * 100) : 0;
              return (
                <div key={`reason-${r.name || i}`} className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 w-5 text-right shrink-0">{i + 1}.</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm font-medium text-slate-700">{r.name}</span>
                      <span className="text-xs text-slate-500">{r.value} quotes ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {noReasonCount > 0 && (
            <p className="text-xs text-slate-400 mt-3">{noReasonCount} quote(s) had no reason recorded.</p>
          )}
        </div>
      )}

      <ReportTable
        headers={['Quote No.', 'Date', 'Client', 'Job Titles', 'Status', 'Reason', 'Value']}
        rows={items.map(r => [
          r.quote_number,
          fmtDate(r.date),
          r.client_name,
          r.job_titles,
          r.status,
          r.reason || '-',
          fmt(r.value),
        ])}
        footer={['', '', '', '', '', 'TOTAL LOST', fmt(total_value_lost)]}
        testId="lost-quotes-table"
      />
    </div>
  );
}
